import { FC, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Autocomplete as MuiAutocomplete } from '@mui/material';
import {
  UploadFile as UploadFileIcon,
  Assignment as AssignIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useDataProvider, useGetList, useGetMany, useNotify, useTranslate } from 'react-admin';
import {
  BatchAssignToChannelResult,
  BatchAssignToChannelSkipped,
  useBatchAssignUnitsToChannel,
} from 'services/api/useBatchAssignUnitsToChannel';

export interface BatchAssignToChannelModalProps {
  open: boolean;
  onClose: () => void;
  /** Pre-populate serials in the textarea (from selected units in bulk action) */
  initialSerials?: string[];
  /**
   * Full unit rows for pre-selected units. Used to derive source-channel
   * diversity so the user can choose which channels to move from.
   */
  initialUnits?: Array<{ serial: string; update_channel_id: number | null }>;
  /** Lock the channel selector to a specific channel id */
  lockedChannelId?: number;
  /**
   * Hardware revision ids of the selected units.
   * - length === 1 → filter channel list to that revision only.
   * - length  >  1 → mixed hardware; channel picking is blocked with a warning.
   * - empty / undefined → no filtering (e.g. opened from the channel detail page).
   */
  hardwareRevisionIds?: number[];
  onSuccess?: (updatedCount: number) => void;
}

interface ChannelOption {
  id: number;
  name: string;
  hardware_revision_id?: number;
  hardware_revision_name?: string;
}

/** Used in the "move from" multi-select. id=null means "Unassigned". */
interface SourceChannelOption {
  id: number | null;
  name: string;
}

const UNASSIGNED_ID = null;

type TabValue = 'serials' | 'csv';

const BatchAssignToChannelModal: FC<BatchAssignToChannelModalProps> = ({
  open,
  onClose,
  initialSerials = [],
  initialUnits = [],
  lockedChannelId,
  hardwareRevisionIds = [],
  onSuccess,
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { batchAssign, loading } = useBatchAssignUnitsToChannel();

  const [tab, setTab] = useState<TabValue>('serials');
  const [serials, setSerials] = useState<string>(initialSerials.join('\n'));
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedChannel, setSelectedChannel] = useState<ChannelOption | null>(null);
  const [results, setResults] = useState<BatchAssignToChannelResult | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [expandedUpdated, setExpandedUpdated] = useState(false);
  const [expandedSkipped, setExpandedSkipped] = useState(false);
  const [expandedNotFound, setExpandedNotFound] = useState(false);

  // Source-channel filter: which channels to move units FROM.
  // null means "all channels" (no filtering). Array means explicit selection.
  const [selectedSourceChannels, setSelectedSourceChannels] = useState<SourceChannelOption[] | null>(null);

  // Hardware revision ids discovered from manually typed serials (looked up async).
  const [extraRevisionIds, setExtraRevisionIds] = useState<number[]>([]);
  const [isCheckingRevisions, setIsCheckingRevisions] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync serials and reset source-channel filter every time the modal opens.
  useEffect(() => {
    if (open) {
      setSerials(initialSerials.join('\n'));
      setExtraRevisionIds([]);
      setSelectedSourceChannels(null); // null = "all channels"
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Debounced lookup: whenever the serials textarea changes, resolve the
  // hardware_revision_id of any typed serials so we can warn on mismatch.
  useEffect(() => {
    if (tab !== 'serials') return;

    const parsedSerials = serials
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);

    if (parsedSerials.length === 0) {
      setExtraRevisionIds([]);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    setIsCheckingRevisions(true);

    debounceRef.current = setTimeout(() => {
      dataProvider
        .getList<{ id: number; hardware_revision_id?: number }>('units', {
          pagination: { page: 1, perPage: 500 },
          sort: { field: 'id', order: 'ASC' },
          filter: { serials: parsedSerials },
        })
        .then(({ data }) => {
          const ids = [
            ...new Set(data.map((u) => u.hardware_revision_id).filter((id): id is number => id !== undefined)),
          ];
          setExtraRevisionIds(ids);
        })
        .catch(() => setExtraRevisionIds([]))
        .finally(() => setIsCheckingRevisions(false));
    }, 600);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [serials, tab, dataProvider]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Combine revision ids from pre-selected units and from manually typed serials.
  const allRevisionIds = [...new Set([...hardwareRevisionIds, ...extraRevisionIds])];
  const hasMixedHardware = allRevisionIds.length > 1;
  // Single known revision → filter channels to compatible ones only.
  // Use only the prop-supplied ids for channel filtering so the list stays
  // stable while the user is typing.
  const singleRevisionId = hardwareRevisionIds.length === 1 ? hardwareRevisionIds[0] : undefined;

  // --- Source-channel logic -------------------------------------------------
  // Derive the distinct channels that the pre-selected units currently belong to.
  const sourceChannelOptions: SourceChannelOption[] = useMemo(() => {
    const uniqueIds = [...new Set(initialUnits.map((u) => u.update_channel_id))];
    // We resolve names below after the useGetMany. Build placeholder list for now.
    return uniqueIds.map((id) => ({ id, name: id === UNASSIGNED_ID ? '' : String(id) }));
  }, [initialUnits]);

  const nonNullSourceIds = useMemo(
    () => sourceChannelOptions.map((o) => o.id).filter((id): id is number => id !== null),
    [sourceChannelOptions],
  );

  // Fetch channel names for the source ids (serves from cache if already loaded).
  const { data: sourceChannelRecords = [] } = useGetMany<ChannelOption>(
    'update_channels',
    { ids: nonNullSourceIds },
    { enabled: nonNullSourceIds.length > 0 },
  );

  // Build the fully-named source channel options list.
  const namedSourceOptions: SourceChannelOption[] = useMemo(() => {
    const nameMap = new Map(sourceChannelRecords.map((c) => [c.id, c.name]));
    return sourceChannelOptions.map((o) =>
      o.id === UNASSIGNED_ID
        ? { id: UNASSIGNED_ID, name: '' } // name filled via translate below
        : { id: o.id, name: nameMap.get(o.id as number) ?? String(o.id) },
    );
  }, [sourceChannelOptions, sourceChannelRecords]);

  // Show the source-channel selector only when there are multiple distinct source channels.
  const showSourceChannelSelector = namedSourceOptions.length > 1;

  // The currently active source filter (null = all).
  const activeSourceChannels: SourceChannelOption[] | null =
    selectedSourceChannels !== null && selectedSourceChannels.length > 0 ? selectedSourceChannels : null;

  // Build a serial → update_channel_id map from initialUnits for submit-time filtering.
  const serialToChannelId = useMemo(
    () => new Map(initialUnits.map((u) => [u.serial, u.update_channel_id])),
    [initialUnits],
  );

  // When a source filter is active, compute the filtered payload.
  const filteredPayloadSerials = useMemo((): string[] | null => {
    if (!activeSourceChannels) return null; // no filtering
    const allowedIds = new Set(activeSourceChannels.map((o) => o.id));
    return serials
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean)
      .filter((serial) => {
        // If the serial is in initialUnits, apply the channel filter.
        if (serialToChannelId.has(serial)) {
          return allowedIds.has(serialToChannelId.get(serial) ?? null);
        }
        // Manually typed serials (not from pre-selection) are always included.
        return true;
      });
  }, [activeSourceChannels, serials, serialToChannelId]);

  const { data: channels = [], isLoading: channelsLoading } = useGetList<ChannelOption>(
    'update_channels',
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: 'name', order: 'ASC' },
      filter: {
        active: true,
        ...(singleRevisionId !== undefined ? { hardware_revision_id: singleRevisionId } : {}),
      },
    },
    { enabled: open && !lockedChannelId },
  );

  const { data: lockedChannelData } = useGetList<ChannelOption>(
    'update_channels',
    {
      pagination: { page: 1, perPage: 1 },
      sort: { field: 'name', order: 'ASC' },
      filter: { id: lockedChannelId },
    },
    { enabled: open && !!lockedChannelId },
  );

  const effectiveChannel: ChannelOption | null =
    lockedChannelId && lockedChannelData?.[0] ? lockedChannelData[0] : selectedChannel;

  const isPhaseResults = results !== null;

  const canSubmit: boolean = (() => {
    if (hasMixedHardware || isCheckingRevisions) return false;
    if (!effectiveChannel) return false;
    if (tab === 'serials') return serials.trim().length > 0;
    return selectedFile !== null;
  })();

  const handleTabChange = (_: React.SyntheticEvent, value: TabValue) => {
    setTab(value);
    setSelectedFile(null);
    setValidationError(null);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setSelectedFile(file);
    event.target.value = '';
  };

  const handleSubmit = async () => {
    if (!effectiveChannel) return;
    setValidationError(null);

    const payload: string[] | File =
      tab === 'csv' && selectedFile
        ? selectedFile
        : (filteredPayloadSerials ??
          serials
            .split('\n')
            .map((s) => s.trim())
            .filter(Boolean));

    try {
      const result = await batchAssign(effectiveChannel.id, payload);
      setResults(result);
      onSuccess?.(result.updated.length);
    } catch (err: unknown) {
      // Flat `{ field: string }` — see the note in `BatchAssignModal`; the provider has already
      // picked the first message per field (TVR-2148).
      const httpErr = err as { status?: number; body?: { errors?: Record<string, string> } };
      if (httpErr?.status === 422 && httpErr?.body?.errors) {
        const firstError = Object.values(httpErr.body.errors)[0];
        setValidationError(firstError ?? translate('resources.units.batch_assign_channel.error_generic'));
      } else {
        notify('resources.units.batch_assign_channel.error_generic', { type: 'error' });
        handleClose();
      }
    }
  };

  const handleClose = () => {
    setTab('serials');
    setSerials(initialSerials.join('\n'));
    setSelectedFile(null);
    setSelectedChannel(null);
    setSelectedSourceChannels(null);
    setResults(null);
    setValidationError(null);
    setExpandedUpdated(false);
    setExpandedSkipped(false);
    setExpandedNotFound(false);
    onClose();
  };

  const totalCount = results ? results.updated.length + results.skipped.length + results.not_found.length : 0;

  return (
    <Dialog open={open} onClose={isPhaseResults ? handleClose : handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{translate('resources.units.batch_assign_channel.dialog_title')}</DialogTitle>

      <DialogContent>
        {!isPhaseResults && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {/* Mixed hardware warning — channel picking is blocked */}
            {hasMixedHardware && (
              <Alert severity="warning">
                {translate('resources.units.batch_assign_channel.warning_mixed_hardware')}
              </Alert>
            )}

            {/* Source-channel selector — shown when units come from multiple channels */}
            {showSourceChannelSelector && !hasMixedHardware && (
              <Alert severity="info" sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {translate('resources.units.batch_assign_channel.info_multiple_source_channels')}
                </Typography>
                <MuiAutocomplete<SourceChannelOption, true>
                  multiple
                  disableCloseOnSelect
                  options={namedSourceOptions}
                  getOptionLabel={(o) =>
                    o.id === UNASSIGNED_ID
                      ? translate('resources.units.batch_assign_channel.source_channel_unassigned')
                      : o.name
                  }
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  value={selectedSourceChannels ?? namedSourceOptions}
                  onChange={(_, value) => {
                    // If all options are selected, treat as "all channels" (null).
                    setSelectedSourceChannels(value.length === namedSourceOptions.length ? null : value);
                  }}
                  disabled={loading}
                  sx={{ minWidth: 280, mt: 0.5, bgcolor: 'background.paper', borderRadius: 1 }}
                  renderOption={(props, option, { selected }) => (
                    <li {...props} key={option.id ?? 'unassigned'}>
                      <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
                      {option.id === UNASSIGNED_ID
                        ? translate('resources.units.batch_assign_channel.source_channel_unassigned')
                        : option.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={translate('resources.units.batch_assign_channel.source_channel_label')}
                      size="small"
                      placeholder={
                        selectedSourceChannels === null
                          ? translate('resources.units.batch_assign_channel.source_channel_all')
                          : undefined
                      }
                    />
                  )}
                />
                {activeSourceChannels && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    {translate('resources.units.batch_assign_channel.source_channel_filtered_count', {
                      count: filteredPayloadSerials?.length ?? 0,
                      total: serials.split('\n').filter((s) => s.trim()).length,
                    })}
                  </Typography>
                )}
              </Alert>
            )}

            {/* Channel selector */}
            {lockedChannelId ? (
              <TextField
                label={translate('resources.units.batch_assign_channel.channel_label')}
                value={lockedChannelData?.[0]?.name ?? '…'}
                disabled
                size="small"
                fullWidth
              />
            ) : (
              <MuiAutocomplete<ChannelOption>
                options={channels}
                getOptionLabel={(o) => o.name}
                groupBy={(o) => o.hardware_revision_name ?? ''}
                value={selectedChannel}
                onChange={(_, value) => setSelectedChannel(value)}
                loading={channelsLoading}
                disabled={loading || hasMixedHardware}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={translate('resources.units.batch_assign_channel.channel_label')}
                    size="small"
                  />
                )}
              />
            )}

            {/* Tab switcher */}
            <Tabs value={tab} onChange={handleTabChange}>
              <Tab value="serials" label={translate('resources.units.batch_assign_channel.tab_serials')} />
              <Tab value="csv" label={translate('resources.units.batch_assign_channel.tab_csv')} />
            </Tabs>

            {/* Serials tab */}
            {tab === 'serials' && (
              <TextField
                label={translate('resources.units.batch_assign_channel.serials_label')}
                placeholder={translate('resources.units.batch_assign_channel.serials_placeholder')}
                multiline
                minRows={5}
                fullWidth
                value={serials}
                onChange={(e) => setSerials(e.target.value)}
                disabled={loading}
              />
            )}

            {/* CSV tab */}
            {tab === 'csv' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <input ref={fileInputRef} type="file" accept=".csv" hidden onChange={handleFileChange} />
                <Button
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  {selectedFile ? selectedFile.name : translate('resources.units.batch_assign_channel.file_label')}
                </Button>
                <Typography variant="caption" color="text.secondary">
                  {translate('resources.units.batch_assign_channel.file_helper')}
                </Typography>
              </Box>
            )}

            {validationError && <Alert severity="error">{validationError}</Alert>}
          </Box>
        )}

        {isPhaseResults && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {translate('resources.units.batch_assign_channel.results_summary', {
                updated: results.updated.length,
                skipped: results.skipped.length,
                not_found: results.not_found.length,
                total: totalCount,
              })}
            </Typography>

            {results.updated.length > 0 && (
              <Box>
                <Alert
                  severity="success"
                  action={
                    <IconButton size="small" onClick={() => setExpandedUpdated((v) => !v)}>
                      {expandedUpdated ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  }
                >
                  {translate('resources.units.batch_assign_channel.results_updated_heading', {
                    count: results.updated.length,
                  })}
                </Alert>
                <Collapse in={expandedUpdated} timeout={10}>
                  <List dense>
                    {results.updated.map((serial) => (
                      <ListItem key={serial}>
                        <ListItemText primary={serial} />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </Box>
            )}

            {results.skipped.length > 0 && (
              <Box>
                <Alert
                  severity="warning"
                  action={
                    <IconButton size="small" onClick={() => setExpandedSkipped((v) => !v)}>
                      {expandedSkipped ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  }
                >
                  {translate('resources.units.batch_assign_channel.results_skipped_heading', {
                    count: results.skipped.length,
                  })}
                </Alert>
                <Collapse in={expandedSkipped} timeout={10}>
                  <List dense>
                    {results.skipped.map((item: BatchAssignToChannelSkipped) => {
                      let translatedReason: string;
                      if (item.reason === 'Hardware revision mismatch') {
                        if (item.hardware_name || item.hardware_revision_name) {
                          translatedReason = translate(
                            'resources.units.batch_assign_channel.skip_reason_hardware_revision_mismatch_details',
                            {
                              hardware: item.hardware_name ?? '?',
                              hardware_revision: item.hardware_revision_name ?? '?',
                            },
                          );
                        } else {
                          translatedReason = translate(
                            'resources.units.batch_assign_channel.skip_reason_hardware_revision_mismatch',
                          );
                        }
                      } else if (item.reason === 'No configuration found') {
                        translatedReason = translate(
                          'resources.units.batch_assign_channel.skip_reason_no_configuration_found',
                        );
                      } else {
                        translatedReason = item.reason;
                      }
                      return (
                        <ListItem key={item.serial}>
                          <ListItemText primary={item.serial} secondary={translatedReason} />
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              </Box>
            )}

            {results.not_found.length > 0 && (
              <Box>
                <Alert
                  severity="error"
                  action={
                    <IconButton size="small" onClick={() => setExpandedNotFound((v) => !v)}>
                      {expandedNotFound ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  }
                >
                  {translate('resources.units.batch_assign_channel.results_not_found_heading', {
                    count: results.not_found.length,
                  })}
                </Alert>
                <Collapse in={expandedNotFound} timeout={10}>
                  <List dense>
                    {results.not_found.map((serial) => (
                      <ListItem key={serial}>
                        <ListItemText primary={serial} />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {!isPhaseResults && (
          <>
            <Button onClick={handleClose} disabled={loading}>
              {translate('resources.units.batch_assign_channel.cancel_button')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!canSubmit || loading}
              startIcon={loading ? <CircularProgress size={16} /> : <AssignIcon />}
              sx={{
                'bgcolor': 'primary.main',
                'color': 'secondary.main',
                'borderRadius': '30px',
                'borderTopRightRadius': '10px',
                'textTransform': 'none',
                '&:hover': {
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'secondary.main',
                  color: 'secondary.main',
                },
              }}
            >
              {translate('resources.units.batch_assign_channel.submit_button')}
            </Button>
          </>
        )}
        {isPhaseResults && (
          <Button onClick={handleClose}>{translate('resources.units.batch_assign_channel.close_button')}</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BatchAssignToChannelModal;
